import { autoinject } from "aurelia-dependency-injection";
import { transient } from "aurelia-dependency-injection";
import { Router } from "aurelia-router";
import { DataGrid, RowItem } from "../../../components/data-grid/data-grid";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import * as Enumerable from "linq";
import {
  NotificationClientService,
  NotificationType
} from "../../../../services/basics/notification-client.service";
import { DataGridEvents } from "../../../components/data-grid/data-grid-events";
import { Queries } from "../queries";
import { DataGridColumn, DataType } from "../../../../models/queries/queries.models";
import { EventLogModel } from "../../../../models/event-log/event-log.models";

@autoinject
@transient()
export class EventLogsNavbar {
  private _model: { selectedItem: any; url: string; datagrid: DataGrid; };
  private _queries: Queries;
  private showMoreColumnKey: string = "eventlogs-navbar:showMore";

  private _onDoubleClickEventListener = ($event: CustomEvent) => {
    this.resolveRoute($event.detail.row);
  }

  constructor(
    protected router: Router,
    private notificationService: NotificationClientService,
    private dialogClientService: DialogClientService
  ) { }

  activate(model: { selectedItem: any; url: string; datagrid: DataGrid }) {
    this._model = model;
    this._model.datagrid.addEventListener(
      DataGridEvents.doubleClick,
      this._onDoubleClickEventListener
    );
  }

  getShowMoreFixedColumn() {
    return {
      dataType: DataType.CustomTemplate,
      value:
        "<a class='request-link' click.trigger='openDialog(item)'>Show More</a>",
      parameter: {
        openDialog: (item: EventLogModel) => {
          this.dialogClientService.show(
            DialogServiceConstants.URLs.EventLogDetailsDialog,
            item
          );
        },
        item: null
      },
      name: "showMore",
      title: "",
      instancePerRow: true,
      disableSorting: true,
      sortOrder: 1,
      columnOrder: 1,
      isHidden: false,
      key: this.showMoreColumnKey
    };
  }

  bind(bindingContext, overrideContext) {
    if (overrideContext && overrideContext.parentOverrideContext && overrideContext.parentOverrideContext.bindingContext) {
      const queries = overrideContext.parentOverrideContext.bindingContext;

      if (!this._queries) {
        this._queries = queries;

        if (this.router.currentInstruction.config && typeof this.router.currentInstruction.config.messageMaxLength === "number") {
          this._queries.onColumnsPrepared = (columns: DataGridColumn[]) => {
            const column = Enumerable.from(columns).firstOrDefault(x => x.name === "message");
            if (column) {
              const maxLength = this.router.currentInstruction.config.messageMaxLength;
              column.valueConverter = (value) => {
                let message = value || "";

                if (message.length > maxLength) {
                  message = message.substring(0, maxLength);
                  message += " ...";
                }
                return message;
              };
            }

            if (typeof this.router.currentInstruction.config.onColumnsPrepared === "function") {
              this.router.currentInstruction.config.onColumnsPrepared(columns);
            }
          }

          if (Array.isArray(this._queries.fixedColumns)) {
            if (!this._queries.fixedColumns.some(c => c["key"] == this.showMoreColumnKey)) {
              this._queries.fixedColumns.push(this.getShowMoreFixedColumn());
            }
          } else {
            this._queries.fixedColumns = [this.getShowMoreFixedColumn()];
          }
        }
      }
    }
  }

  detached() {
    this._model.datagrid.removeEventListener(
      DataGridEvents.doubleClick,
      this._onDoubleClickEventListener
    );
  }

  public resolveRoute(rowItem: RowItem) {
    if (rowItem) {
      const resolveRoute = rowItem.item.resolveRoute;
      if (typeof resolveRoute == "string") {
        const isDialog = Object.keys(DialogServiceConstants.URLs).some(
          k => resolveRoute === DialogServiceConstants.URLs[k]
        );

        if (isDialog) {
          const model: {
            selectedRow: RowItem;
            url: string;
            datagrid: DataGrid;
          } = {
            selectedRow: rowItem,
            url: this._model.url,
            datagrid: this._model.datagrid
          };

          this.dialogClientService.show(resolveRoute, model);
        } else {
          this.router.navigate(resolveRoute);
        }
      }
    }
  }

  changeStatus() {
    const rows = this._model.datagrid
      .getSelectedRowsCollection()
      .map(x => x.item);

      if (rows.length == 0) {
        this.notificationService.show(
          "Error",
          "Must select at least one entity",
          NotificationType.Warning
        );
        return;
      }

    const allStatuses = Enumerable.from(rows)
      .select(x => x.statusId)
      .distinct()
      .toArray();
    const allStatusReasons = Enumerable.from(rows)
      .select(x => x.statusReasonId)
      .distinct()
      .toArray();
    if (allStatuses.length > 1 || allStatusReasons.length > 1) {
      this.notificationService.show(
        "Error",
        "Status and Status Reason must be the same for all entities selected",
        NotificationType.Warning
      );
      return;
    }
    this.dialogClientService.show(
      DialogServiceConstants.URLs.ChangeLogsStatusDialog,
      {
        ids: this._model.datagrid.getSpecificIds(),
        reload: () => this._model.datagrid.reloadData(),
        statusId: allStatuses[0],
        statusReasonId: allStatusReasons[0]
      }
    );
  }
}
